"use strict";
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.startSetInterview = exports.startRemoveGroupedObjects = exports.startAddGroupedObjects = exports.addConsent = exports.initNavigationService = exports.startNavigate = exports.startNavigateWithUpdateCallback = exports.startUpdateInterview = exports.setInterviewState = exports.updateInterviewState = exports.updateInterviewData = exports.validateAndPrepareSection = exports.asyncDispatchQueue = void 0;
const set_1 = __importDefault(require("lodash/set"));
const get_1 = __importDefault(require("lodash/get"));
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const unset_1 = __importDefault(require("lodash/unset"));
const bowser_1 = __importDefault(require("bowser"));
const p_queue_1 = __importDefault(require("p-queue"));
/* eslint-disable-next-line */
const fetchRetry = require('@zeit/fetch-retry')(require('node-fetch'));
// TODO Default options for retry are as high as 15 seconds, during which the
// user get no feedback. Since update requests are queued in that time, are
// sequential and it is not possible to empty the queue, the requests will try
// to execute for n*15 seconds, during which time the user may have refreshed
// the page and will not see that the updated values have been udpated. With 4
// retries, the user will get feedback within 3 seconds and the queue will empty
// much faster, reducing the risk of invisible updates. The risk is still
// present if the server is quickly back online and the user is fast enough.
const fetch = (url, opts) => __awaiter(void 0, void 0, void 0, function* () {
    return yield fetchRetry(url, Object.assign(Object.assign({ retry: { retries: 4 } }, opts)));
});
const i18n_config_1 = __importDefault(require("../config/i18n.config"));
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
const surveyHelper = __importStar(require("evolution-common/lib/utils/helpers"));
const utils_1 = require("./utils");
const LoadingState_1 = require("./LoadingState");
const errorHandling_1 = require("../services/errorManagement/errorHandling");
const survey_1 = require("../store/survey");
const NavigationService_1 = require("evolution-common/lib/services/questionnaire/sections/NavigationService");
/**
 * Class used to manage a queue of asynchronous update functions, to ensure they
 * are run sequentially.
 *
 * FIXME We used to use the `redux-async-queue` package, but it is old and not
 * compatible with the current redux version. According to the documentation
 * when doing the update, it should not be required to use a queue anymore. The
 * `Thunk` should handle. Either, we are not using redux properly in this case,
 * or the Thunk is not correctly configured, but we need it to avoid concurrent
 * updates where one is lost. But we should not use this approach, or at least
 * investigate other ways. See if we can prevent it by using the
 * `@reduxjs/toolkit` package (see issue
 * https://github.com/chairemobilite/transition/issues/1154 in chaire-lib)
 */
class AsyncDispatchQueue {
    constructor() {
        // Create a queue with concurrency of 1 to ensure sequential processing
        this.interviewUpdateQueue = new p_queue_1.default({ concurrency: 1 });
        // Utility function to add a task to the queue and return a promise
        this.enqueueTask = (task) => {
            return this.interviewUpdateQueue.add(task);
        };
        // For debugging purposes
        this.getQueueSize = () => {
            return this.interviewUpdateQueue.size;
        };
        this.getPendingCount = () => {
            return this.interviewUpdateQueue.pending;
        };
    }
}
// Exported for the SurveyAdmin action and in case we need to monitor the queue
exports.asyncDispatchQueue = new AsyncDispatchQueue();
/**
 * Called whenever an update occurs in interview response or when section is
 * switched to. It will run the validations and side effects for the requested
 * section and prepare the widgets for the next display cycle of the section. It
 * returns the interview, with the widget statuses set for the next cycle, as
 * well as the side effects that should be applied to the interview.
 *
 * This function should only be called by redux actions, in this file and the
 * admin one.
 *
 * TODO: unit test
 *
 * @param {string} sectionShortname The section shortname to prepare
 * @param {UserRuntimeInterviewAttributes} _interview The interview to prepare.
 * It will not be modified
 * @param {{ [path: string]: boolean }} affectedPaths The paths that were
 * affected by the update (usually the keys of the `valuesByPath` object)
 * @param {{ [path: string]: unknown }} valuesByPath The values by path that
 * were affected by the update
 * @param {boolean} [updateKey] If `true`, the section key will be updated to
 * the current section shortname, forcing a re-render of the widgets. Defaults
 * to `false`
 * @param {CliUser} [user] The user, if available, to use for the side effects
 * @returns A tuple with the updated interview and the values by path that
 * should be applied to the interview
 */
const validateAndPrepareSection = (sectionShortname, _interview, affectedPaths, valuesByPath, updateKey = false, user) => {
    let interview = (0, cloneDeep_1.default)(_interview);
    let currentValuesByPath = valuesByPath;
    let needToUpdate = true; // will stay true if an assigned value changed the initial value after a conditional failed
    let updateCount = 0;
    while (needToUpdate && updateCount < 10 /* security against infinite loops */) {
        const { updatedInterview, updatedValuesByPath, needUpdate } = (0, utils_1.prepareSectionWidgets)(sectionShortname, interview, affectedPaths, valuesByPath, updateKey, user);
        needToUpdate = needUpdate;
        interview = updatedInterview;
        currentValuesByPath = updatedValuesByPath;
        updateCount++;
    }
    return [interview, currentValuesByPath];
};
exports.validateAndPrepareSection = validateAndPrepareSection;
// Get the widget paths that correspond to a given test function
const getWidgetsWithData = (interview, test) => {
    const widgetsWithData = [];
    // For each widget status, if the widget passes the test, it is added to the array
    const widgets = interview.widgets || {};
    for (const widget of Object.values(widgets)) {
        if (test(widget)) {
            widgetsWithData.push(widget.path);
        }
    }
    const groups = interview.groups || {};
    for (const groupName in groups) {
        const group = groups[groupName];
        for (const objectId in group) {
            const objectWidgets = group[objectId];
            for (const widget of Object.values(objectWidgets)) {
                if (test(widget)) {
                    widgetsWithData.push(widget.path);
                }
            }
        }
    }
    return widgetsWithData.length === 0 ? undefined : widgetsWithData;
};
// Get the widget paths that are not visible in the interview.
const getHiddenWidgets = (interview) => getWidgetsWithData(interview, (widget) => !widget.isVisible);
// Get the widget paths that are visible and invalid in the interview.
const getInvalidWidgets = (interview) => getWidgetsWithData(interview, (widget) => widget.isVisible && !widget.isValid);
const enhanceUserActionOnUpdate = (interview, userAction) => {
    if (userAction && userAction.type === 'buttonClick') {
        // Save the hidden widgets when a button is clicked, not on section
        // change as this function is run with the next section, but the hidden
        // widgets should be for the previous one. The navigate action will set
        // the hidden widgets for the previous section.
        userAction.hiddenWidgets = getHiddenWidgets(interview);
        // Save the invalid widgets when a button is clicked. The invalidity
        // status may not be in the valuesByPath if it was previously set as
        // invalid and there is no change to the status, so we just sent all
        // visible invalid widgets here.
        userAction.invalidWidgets = getInvalidWidgets(interview);
    }
};
/**
 * Update the interview fields with the modified values.
 *
 * DO NOT CALL from any other part of the code! Only the redux action should
 * call this function.
 *
 * @param interview The interview to update. Its values will be modified
 * @param updatedData The values by path, path to unset and userAction to apply
 * @returns A key/value object where the keys are all paths affected by the
 * update and true as value, for easier lookup
 */
const updateInterviewData = (interview, updatedData) => {
    const affectedPaths = {};
    if (Array.isArray(updatedData.unsetPaths)) {
        // unsetPaths if array (each path in array has to be deleted)
        for (let i = 0, count = updatedData.unsetPaths.length; i < count; i++) {
            const path = updatedData.unsetPaths[i];
            affectedPaths[path] = true;
            (0, unset_1.default)(interview, path);
        }
    }
    if (updatedData.valuesByPath) {
        if (updatedData.valuesByPath['_all'] === true) {
            affectedPaths['_all'] = true;
        }
        for (const path in updatedData.valuesByPath) {
            if (path !== '_all') {
                affectedPaths[path] = true;
                if (updatedData.valuesByPath[path] === undefined) {
                    (0, unset_1.default)(interview, path);
                }
                else {
                    (0, set_1.default)(interview, path, updatedData.valuesByPath[path]);
                }
            }
        }
    }
    if (updatedData.userAction && updatedData.userAction.type === 'widgetInteraction') {
        const path = updatedData.userAction.path;
        if (path) {
            affectedPaths[path] = true;
            (0, set_1.default)(interview, path, updatedData.userAction.value);
        }
    }
    return affectedPaths;
};
exports.updateInterviewData = updateInterviewData;
const updateInterviewCallback = (dispatch_1, getState_1, ...args_1) => __awaiter(void 0, [dispatch_1, getState_1, ...args_1], void 0, function* (dispatch, getState, { sectionShortname: requestedSectionShortname, valuesByPath = {}, unsetPaths = [], interview: initialInterview, gotoFunction, userAction } = {}, callback) {
    var _a;
    try {
        const { auth, survey } = getState();
        const user = (auth === null || auth === void 0 ? void 0 : auth.user) || undefined;
        const navigation = survey === null || survey === void 0 ? void 0 : survey.navigation;
        const interview = initialInterview
            ? initialInterview
            : (0, cloneDeep_1.default)(survey === null || survey === void 0 ? void 0 : survey.interview);
        dispatch((0, LoadingState_1.incrementLoadingState)());
        if (valuesByPath && Object.keys(valuesByPath).length > 0) {
            surveyHelper.devLog('Update interview and section with values by path', JSON.parse(JSON.stringify(valuesByPath)));
        }
        const affectedPaths = (0, exports.updateInterviewData)(interview, { valuesByPath, unsetPaths, userAction });
        // TODO An Interview object could have a getActiveSection function to get this value
        const sectionShortname = requestedSectionShortname
            ? requestedSectionShortname
            : ((_a = navigation === null || navigation === void 0 ? void 0 : navigation.currentSection) === null || _a === void 0 ? void 0 : _a.sectionShortname) || '';
        surveyHelper.devLog('Update section', sectionShortname);
        const [updatedInterview, updatedValuesByPath] = (0, exports.validateAndPrepareSection)(sectionShortname, interview, affectedPaths, valuesByPath, false, user);
        if (!updatedInterview.sectionLoaded || updatedInterview.sectionLoaded !== sectionShortname) {
            updatedValuesByPath['sectionLoaded'] = sectionShortname;
            updatedInterview.sectionLoaded = sectionShortname;
        }
        // convert undefined values to unset (delete) because stringify will remove undefined values:
        for (const path in updatedValuesByPath) {
            if (updatedValuesByPath[path] === undefined) {
                unsetPaths.push(path);
            }
        }
        // No changes to send to the server, just update the state and quit
        if ((0, isEqual_1.default)(updatedValuesByPath, { _all: true }) && (0, LodashExtensions_1._isBlank)(unsetPaths) && (0, LodashExtensions_1._isBlank)(userAction)) {
            // '_all' means the "save" button was clicked and the form was submitted, so the form may not follow the normal form change workflow
            dispatch((0, exports.updateInterviewState)((0, cloneDeep_1.default)(updatedInterview), {}, true));
            if (typeof callback === 'function') {
                callback(updatedInterview);
            }
            return null;
        }
        // The interview has been validated and prepared, see if we need to add anything to the user action
        enhanceUserActionOnUpdate(updatedInterview, userAction);
        // Send update response to the server
        const response = yield fetch('/api/survey/updateInterview', {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                id: updatedInterview.id,
                interviewId: interview.uuid,
                participant_id: updatedInterview.participant_id,
                valuesByPath: updatedValuesByPath,
                unsetPaths: unsetPaths,
                userAction
            })
        });
        if (response.status === 200) {
            const body = yield response.json();
            if ((body.status === 'success' || body.status === 'invalid') &&
                body.interviewId === updatedInterview.uuid) {
                // Reset server validation for any field updated
                // TODO This shouldn't be done here in this manner. See https://github.com/chairemobilite/transition/issues/1245
                const currentServerErrors = Object.assign({}, getState().survey.errors || {});
                for (const path in currentServerErrors) {
                    if (updatedValuesByPath[`response.${path}`] || unsetPaths[`response.${path}`]) {
                        delete currentServerErrors[path];
                    }
                }
                const newServerErrors = body.status === 'invalid' ? body.messages : undefined;
                // Update the interview with server assigned values if applicable
                let serverUpdatedInterview = updatedInterview;
                if (body.updatedValuesByPath && Object.keys(body.updatedValuesByPath).length > 0) {
                    const serverAffectedPath = {};
                    for (const path in body.updatedValuesByPath) {
                        serverAffectedPath[path] = true;
                        (0, set_1.default)(updatedInterview, path, body.updatedValuesByPath[path]);
                    }
                    // FIXME Here, it used to re-read the _activeSection from
                    // interview it case it was modified by the server, but
                    // since we don't support _activeSection anymore, we need a
                    // way to let the server change the active section in case
                    // of dire necessity, but all logic should rather aim to be
                    // described in the section configuration. But we may want a
                    // way to let the server allow to trigger a navigation
                    // again, or maybe the entire navigation will be done
                    // server-side eventually.
                    // Need to update the widget status with server data, there should be no side-effect, so no loop update here
                    serverUpdatedInterview = (0, exports.validateAndPrepareSection)(sectionShortname, updatedInterview, serverAffectedPath, body.updatedValuesByPath, true, user)[0];
                    // FIXME When the interview update process is better
                    // documented and understood, see why we need this, but when
                    // the section is changed by the server, not doing this
                    // block results in empty survey rendering.
                    if (!serverUpdatedInterview.sectionLoaded ||
                        serverUpdatedInterview.sectionLoaded !== sectionShortname) {
                        serverUpdatedInterview.sectionLoaded = sectionShortname;
                    }
                }
                dispatch((0, exports.updateInterviewState)((0, cloneDeep_1.default)(serverUpdatedInterview), Object.assign(currentServerErrors, newServerErrors), updatedValuesByPath['_all'] === true));
                if (typeof callback === 'function') {
                    callback(updatedInterview);
                }
            }
            else if (body.status === 'redirect') {
                // Logout from the application and redirect to the specified URL
                // TODO Can't use the dispatch action, as the startLogout
                // requires the navigate function and here it is optional. This
                // fetch logout should be in a helper.
                fetch('/logout', { credentials: 'include' });
                window.location.href = body.redirectUrl;
            }
            else {
                // we need to do something if no interview is returned (error)
            }
        }
        else {
            console.log(`Update interview: wrong response status: ${response.status}`);
            yield (0, errorHandling_1.handleHttpOtherResponseCode)(response.status, dispatch, gotoFunction);
        }
    }
    catch (error) {
        console.log('Error updating interview', error);
        (0, errorHandling_1.handleClientError)(error instanceof Error ? error : new Error(String(error)), {
            navigate: gotoFunction,
            interviewId: getState().survey.interview.id
        });
    }
    finally {
        // Loading state needs to be decremented, so the page can be updated
        dispatch((0, LoadingState_1.decrementLoadingState)());
    }
});
/**
 * Redux action to call with a dispatch to update the interview in the store.
 *
 * DO NOT CALL from any other part of the code! Only the redux action should
 * call this function.
 *
 * @param interview The interview to update
 * @param errors The current errors in the interview
 * @param submitted FIXME Need to document this. Is it when the form was
 * officially submitted with a next button?
 * @returns
 */
const updateInterviewState = (interview, errors = {}, submitted = false) => ({
    type: survey_1.SurveyActionTypes.UPDATE_INTERVIEW,
    interviewLoaded: true,
    interview,
    errors,
    submitted
});
exports.updateInterviewState = updateInterviewState;
/**
 * Redux action to call with a dispatch to set the interview in the store.
 *
 * DO NOT CALL from any other part of the code! Only the redux action should
 * call this function.
 *
 * @param interview The interview to update
 * @param interviewLoaded Whether the interview has been loaded
 * @returns
 */
const setInterviewState = (interview) => ({
    type: survey_1.SurveyActionTypes.SET_INTERVIEW,
    interview,
    interviewLoaded: interview !== undefined
});
exports.setInterviewState = setInterviewState;
/**
 * Redux action to call with a dispatch to send interview updates to the server.
 * This function is not to be called directly by the application, except through
 * redux's dispatch.
 *
 * It will schedule the call to the interview update callback who runs
 * validations and side effects, updates the interview data on the server and
 * refresh any fields and validations coming from the server. It will update the
 * app's loading state before and after the update call. At the end of the call,
 * the callback function will be called if provided, with the updated interview.
 * @returns The dispatched action
 */
const startUpdateInterview = (data, callback) => (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
    // Queue the update operation to ensure sequential processing
    return yield exports.asyncDispatchQueue.enqueueTask(() => __awaiter(void 0, void 0, void 0, function* () {
        // If there's a _activeSection explicitly set in valuesByPath, this is a legacy navigation request
        // Convert it to use the navigation service instead
        if ((data === null || data === void 0 ? void 0 : data.valuesByPath) && data.valuesByPath['response._activeSection']) {
            console.error('Warning (startUpdateInterview): _activeSection is deprecated and will no longer be supported in the next release. Define proper navigation in section configuration instead, or use the `startNavigate` callback.');
            const targetSection = data.valuesByPath['response._activeSection'];
            // Remove the _activeSection from valuesByPath
            const newValuesByPath = Object.assign({}, data.valuesByPath);
            delete newValuesByPath['response._activeSection'];
            // If there are no other values to update, just navigate
            if (Object.keys(newValuesByPath).length === 0) {
                return dispatch((0, exports.startNavigate)({ requestedSection: { sectionShortname: targetSection } }));
            }
            // Otherwise, update the interview then navigate in the callback
            const updatedData = Object.assign(Object.assign({}, data), { valuesByPath: newValuesByPath });
            return yield updateInterviewCallback(dispatch, getState, updatedData, (updatedInterview) => {
                dispatch((0, exports.startNavigate)({ requestedSection: { sectionShortname: targetSection } }));
                if (callback)
                    callback(updatedInterview);
            });
        }
        // Standard interview update without navigation
        return yield updateInterviewCallback(dispatch, getState, data, callback);
    }));
});
exports.startUpdateInterview = startUpdateInterview;
const navigate = (targetSection) => ({
    type: survey_1.SurveyActionTypes.NAVIGATE,
    targetSection
});
/**
 * Redux action to call with a dispatch to navigate forward in the current
 * interview
 *
 * DO NOT CALL from any other part of the code! Only the redux action should
 * call this function with proper callback for participant or review interviews.
 *
 * @param {function} [fctUpdateInterview] The update function to call after
 * navigation. For example, a different function can be used for participant or
 * correction survey updates.
 * @returns The dispatched action
 */
const startNavigateWithUpdateCallback = (fctUpdateInterview, { requestedSection, valuesByPath = {}, gotoFunction, userAction } = {}, callback) => (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    try {
        dispatch((0, LoadingState_1.incrementLoadingState)());
        const { survey: surveyState, auth: authState } = getState();
        const { navigation, navigationService, interview } = surveyState;
        // Validate that navigation service and interview are initialized
        if (interview === undefined || navigationService === undefined) {
            return;
        }
        // Validate current section (prepare widgets for current section and see if they are all valid)
        const validateCurrentSection = () => {
            var _a;
            if (requestedSection || navigation === undefined) {
                return [true, interview, {}];
            }
            const [updatedInterview, updatedValuesByPath] = (0, exports.validateAndPrepareSection)(((_a = navigation === null || navigation === void 0 ? void 0 : navigation.currentSection) === null || _a === void 0 ? void 0 : _a.sectionShortname) || '', interview, { _all: true }, { _all: true }, false, (authState === null || authState === void 0 ? void 0 : authState.user) || undefined);
            return [updatedInterview.allWidgetsValid, updatedInterview, updatedValuesByPath];
        };
        const [shouldNavigate, prevSectionInterview, validationValuesByPath] = validateCurrentSection();
        // If the current section is not valid, do not navigate, simply update interview to log invalid widgets
        if (!shouldNavigate) {
            yield dispatch(fctUpdateInterview({
                sectionShortname: ((_a = navigation === null || navigation === void 0 ? void 0 : navigation.currentSection) === null || _a === void 0 ? void 0 : _a.sectionShortname) || '',
                userAction,
                valuesByPath: { _all: true }
            }));
            return;
        }
        const allValuesByPath = Object.assign({}, valuesByPath, validationValuesByPath || {});
        // Delete the _all value by path so that next section does not get validated now
        delete allValuesByPath['_all'];
        // If current section is valid, set interview data and call the navigation service
        (0, exports.updateInterviewData)(interview, { valuesByPath: allValuesByPath });
        const getTargetSectionResult = () => {
            // If there is a requested section or there is no navigation yet, initialize navigation for interview
            if (requestedSection || navigation === undefined) {
                return navigationService.initNavigationState({
                    interview,
                    requestedSection: requestedSection === null || requestedSection === void 0 ? void 0 : requestedSection.sectionShortname,
                    currentSection: navigation === null || navigation === void 0 ? void 0 : navigation.currentSection
                });
            }
            // Otherwise, navigate forward
            return navigationService.navigate({ interview, currentSection: navigation.currentSection });
        };
        const targetSectionResult = getTargetSectionResult();
        // Prepare the values by path for next section
        if (targetSectionResult.valuesByPath) {
            // If the target section has values by path, prepare them
            Object.assign(allValuesByPath, targetSectionResult.valuesByPath);
        }
        // Call the update interview to update to the new section
        yield dispatch(fctUpdateInterview({
            sectionShortname: targetSectionResult.targetSection.sectionShortname,
            valuesByPath: Object.keys(allValuesByPath).length === 0 ? undefined : allValuesByPath,
            userAction: {
                type: 'sectionChange',
                targetSection: targetSectionResult.targetSection,
                // Previous section is considered completed in
                // direct navigation, ie without a requested
                // section, otherwise it does not follow the survey
                // flow
                previousSection: requestedSection === undefined ? navigation === null || navigation === void 0 ? void 0 : navigation.currentSection : undefined,
                hiddenWidgets: getHiddenWidgets(prevSectionInterview)
            }
        }));
        // Update the navigation state
        dispatch(navigate(targetSectionResult.targetSection));
        // Call the callback function
        if (typeof callback === 'function') {
            callback(getState().survey.interview, targetSectionResult.targetSection);
        }
    }
    catch (error) {
        console.error('Error navigating to section', error);
        (0, errorHandling_1.handleClientError)(error instanceof Error ? error : new Error(String(error)), {
            navigate: gotoFunction,
            interviewId: (_b = getState().survey.interview) === null || _b === void 0 ? void 0 : _b.id
        });
    }
    finally {
        dispatch((0, LoadingState_1.decrementLoadingState)());
    }
});
exports.startNavigateWithUpdateCallback = startNavigateWithUpdateCallback;
/**
 * Redux action to call with a dispatch to navigate forward in the current
 * interview for participant interviews. This action includes a call to the
 * interview update callback action, as navigation implies a call to the server
 * and may change data in the interview. Any additional change to the interview
 * can be passed through the `valuesByPath` parameter.
 *
 * @returns The dispatched action
 */
const startNavigate = (options = {}, callback) => (0, exports.startNavigateWithUpdateCallback)(exports.startUpdateInterview, options, callback);
exports.startNavigate = startNavigate;
const initNavigationService = (sections) => ({
    type: survey_1.SurveyActionTypes.INIT_NAVIGATE,
    navigationService: (0, NavigationService_1.createNavigationService)(sections)
});
exports.initNavigationService = initNavigationService;
const addConsent = (consented) => ({
    type: 'ADD_CONSENT',
    consented
});
exports.addConsent = addConsent;
/**
 * Redux action to call with a dispatch to add grouped objects to the interview
 * and optionally update the server.
 *
 * @param newObjectsCount The number of new objects to add
 * @param insertSequence The sequence number to insert the new objects at. To
 * insert at the end, use a negative number
 * @param path The path in the interview that contains the object array to which
 * to add the new objects
 * @param attributes The attributes to assign to the new objects
 * @param callback A callback function to call after the interview has been
 * updated
 * @param returnOnly If true, the action will return the updated values by path
 * instead of dispatching the update to the server
 * @returns The dispatched action
 */
const startAddGroupedObjects = (newObjectsCount, insertSequence, path, attributes = [], callback, returnOnly = false) => {
    surveyHelper.devLog(`Add ${newObjectsCount} grouped objects for path ${path} at sequence ${insertSequence}`);
    return (dispatch, getState) => {
        const interview = (0, cloneDeep_1.default)(getState().survey.interview); // needed because we cannot mutate state
        const changedValuesByPath = surveyHelper.addGroupedObjects(interview, newObjectsCount, insertSequence, path, attributes || []);
        if (returnOnly) {
            return changedValuesByPath;
        }
        else {
            dispatch((0, exports.startUpdateInterview)({ valuesByPath: changedValuesByPath }, callback));
        }
    };
};
exports.startAddGroupedObjects = startAddGroupedObjects;
/**
 * Redux action to call with a dispatch to remove grouped objects from the
 * interview and optionally update the server. Sequences of the remaining
 * objects will be updated to be continuous.
 *
 * @param paths An array of paths to the objects to remove.
 * @param callback A callback function to call after the interview has been
 * updated
 * @param returnOnly If true, the action will return the updated values by path
 * instead of dispatching the update to the server
 * @returns
 */
const startRemoveGroupedObjects = function (paths, callback, returnOnly = false) {
    surveyHelper.devLog('Remove grouped objects at paths', paths);
    return (dispatch, getState) => {
        const interview = (0, cloneDeep_1.default)(getState().survey.interview); // needed because we cannot mutate state
        const [valuesByPath, unsetPaths] = surveyHelper.removeGroupedObjects(interview, paths);
        if (returnOnly) {
            return [valuesByPath, unsetPaths];
        }
        else {
            dispatch((0, exports.startUpdateInterview)({ valuesByPath, unsetPaths }, callback));
        }
    };
};
exports.startRemoveGroupedObjects = startRemoveGroupedObjects;
const startSetInterview = (requestedSection = undefined, surveyUuid = undefined, navigate = undefined, preFilledResponse = undefined) => {
    return (dispatch, _getState) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const browserTechData = bowser_1.default.getParser(window.navigator.userAgent).parse();
            // get the interview from the server for the current user (or create one), or with a specific survey uuid
            const response = yield fetch(`/api/survey/activeInterview${surveyUuid ? `/${encodeURI(surveyUuid)}` : ''}`, {
                credentials: 'include'
            });
            if (response.status === 200) {
                const body = yield response.json();
                // Get the interview from the response
                if (body.interview) {
                    const interview = body.interview;
                    const valuesByPath = {};
                    if (preFilledResponse) {
                        Object.keys(preFilledResponse).forEach((key) => {
                            valuesByPath[`response.${key}`] = preFilledResponse[key];
                        });
                    }
                    // update browser data if different:
                    // TODO We need to track the different browser/user accesses
                    const existingBrowserUa = (0, get_1.default)(interview, 'response._browser._ua', null);
                    const newBrowserUa = browserTechData.getUA();
                    if (existingBrowserUa !== newBrowserUa) {
                        valuesByPath['response._browser'] = browserTechData;
                    }
                    // Set or update the current language if required
                    const previousLanguage = (0, get_1.default)(interview, 'response._language', null);
                    const currentLanguage = i18n_config_1.default.language;
                    if ((0, LodashExtensions_1._isBlank)(previousLanguage) || previousLanguage !== currentLanguage) {
                        valuesByPath['response._language'] = currentLanguage;
                    }
                    // Set the interview in the state first
                    dispatch((0, exports.setInterviewState)(interview));
                    // Then, initialize navigation for the current interview
                    yield dispatch((0, exports.startNavigate)({
                        requestedSection: requestedSection ? { sectionShortname: requestedSection } : undefined,
                        valuesByPath
                    }));
                }
                else {
                    // No interview for this user, create one
                    console.error('Error: Get active interview: no interview was returned, it\'s not supposed to happen');
                }
            }
            else if (response.status === 403) {
                // The interview is frozen, redirect to the survey unavailable page
                // Note: We want to monitor how often this happens.
                console.log('Redirect to unavailable page: interview cannot be accessed for respondent');
                if (navigate) {
                    navigate('/unavailable'); // User goes to 'unavailable' page
                }
                else {
                    window.location.href = '/unavailable'; // User goes to 'unavailable' page
                }
            }
            else {
                // FIXME Maybe handle specific response codes, like 404
                console.log(`Get active interview: wrong response status: ${response.status}`);
                (0, errorHandling_1.handleHttpOtherResponseCode)(response.status, dispatch, navigate);
            }
        }
        catch (err) {
            surveyHelper.devLog('Error fetching interview.', err);
            (0, errorHandling_1.handleClientError)(err instanceof Error ? err : new Error(String(err)), {});
        }
    });
};
exports.startSetInterview = startSetInterview;
//# sourceMappingURL=Survey.js.map